import Visibility from "@mui/icons-material/Visibility";
import {
	Box,
	Checkbox,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	Typography
} from "@mui/material";
import type { AutocompleteRenderOptionState } from "@mui/material/Autocomplete";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { ChangeEvent, HTMLAttributes, SyntheticEvent } from "react";
import { useEffect, useState } from "react";

import CoAppMultiSelect from "@/components/global/components/inputs/CoAppMultiSelect";
import CoAppTextField from "@/components/global/components/inputs/CoAppTextField";
import TagInput from "@/components/tags/TagInput";
import type { Tag } from "@/components/tags/types";

import {
	CoAppActionButton,
	CoAppCancelTextButton,
	CoAppDialog
} from "../../global/styled/global.styled";
import { normalizeLocationPreviewImageUrl } from "../utils/normalization-utils";
import ScreenLocationPreviewEyeIcon from "./ScreenLocationPreviewEyeIcon";
import ScreenLocationPreviewModal from "./ScreenLocationPreviewModal";

export interface Option {
	label: string;
	value: string;
}

export interface PlatformSelectOption {
	id: string | number;
	name: string;
}

export interface ReactionTypeOption {
	id: number;
	friendlyName: string;
	name: string;
}

export interface RuleFieldOption {
	id: number;
	name?: string;
	dataFieldType?: { id: number; name: string };
}

export interface ScreenLocationOption {
	id: string | number;
	name: string;
	/** Location preview image URL from the API (`previewUrl` on the screen location record). */
	previewUrl?: string | null;
	reactionTypes?: ReactionTypeOption[];
	ruleFields?: RuleFieldOption[];
}

/** Group association for create/save payloads. */
export interface RuleGroupOption {
	id: string;
	name: string;
}

export interface CreateRuleFormState {
	ruleName: string;
	ruleDescription: string;
	ruleTags: string[];
	screenLocation: ScreenLocationOption | null;
	/** Empty string means no platform selected yet (create flow). */
	platformId: string | number | "";
	groups?: RuleGroupOption[];
}

export interface CreateRuleModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: CreateRuleFormState) => void;
	initialState?: Partial<CreateRuleFormState>;
	isSaving?: boolean;
	tags?: string[];
	setTags?: (tags: string[]) => void;
	tagsInCurrentOrganization?: Tag[];
	screenLocationOptions?: ScreenLocationOption[];
	maxRuleNameLength?: number;
	maxDescriptionLength?: number;
	platformOptions?: PlatformSelectOption[];
	/** From GET /groups; selection stored as { id, name } on save. */
	groupOptions?: RuleGroupOption[];
	handleSaveChanges?: (updatedTriggers?: unknown, updatedTags?: string[], updatedReactions?: unknown) => void | Promise<void>;
	editedId?: string | number | null;
	platformId?: string | number;
	setPlatformId?: (platformId: string | number) => void;
	platformError?: boolean;
	platformErrorMessage?: string;
	createRuleError?: string | null;
	setCreateRuleError?: (error: string | null) => void;
	/** When set, the Screen Location control cannot be changed. */
	lockScreenLocation?: boolean;
	/** True while screen locations for the selected platform are being fetched. */
	screenLocationsLoading?: boolean;
	/** True while the groups list is being fetched. */
	groupsLoading?: boolean;
	dialogTitle?: string;
	submitButtonLabel?: string;
}

const DEFAULT_MAX_RULE_NAME_LENGTH = 100;
const DEFAULT_MAX_DESCRIPTION_LENGTH = 255;

// QA fix: shared styling so the <Select> fields (Platform, Screen Location)
// match CoAppTextField exactly — same 8px corners, 1px border, teal hover/
// focus halo, and label sizing/color. Combined with `size="small"` on the
// FormControl, this makes every field in the Create Rule modal the same
// height with the label vertically centered (fixes the misaligned dropdowns).
const SELECT_FIELD_SX = {
	"& .MuiOutlinedInput-root": {
		// 8px to match CoAppTextField. (sx `borderRadius` multiplies
		// theme.shape.borderRadius=8px, so the old `2` rendered as 16px.)
		borderRadius: "8px",
		background: "#FFFFFF",
		fontSize: 14,
		fontWeight: 500,
		transition: "border-color 0.18s, box-shadow 0.18s",
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "rgba(13, 31, 48, 0.14)",
			borderWidth: 1,
			transition: "border-color 0.18s"
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "rgba(2, 213, 171, 0.5)"
		},
		"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "#02D5AB",
			borderWidth: 1
		},
		"&.Mui-focused": {
			boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.18)"
		},
		"&.Mui-disabled": {
			background: "rgba(13, 31, 48, 0.03)"
		}
	},
	"& .MuiInputLabel-root": {
		fontSize: 13,
		fontWeight: 500,
		color: "rgba(13, 31, 48, 0.55)",
		"&.Mui-focused": { color: "#057A6D" }
	},
	"& .MuiSelect-select": {
		padding: "10px 12px",
		display: "flex",
		alignItems: "center"
	}
};

// Rounded, soft-shadowed dropdown menu shared by both selects so the open
// menus look identical too.
const SELECT_MENU_PROPS = {
	slotProps: {
		paper: {
			sx: {
				mt: 0.5,
				borderRadius: 2,
				boxShadow: "0 8px 32px rgba(13, 31, 48, 0.10), 0 1px 3px rgba(13, 31, 48, 0.04)"
			}
		}
	}
};

const CreateRuleModal = (props: CreateRuleModalProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		tags,
		setTags,
		maxRuleNameLength = DEFAULT_MAX_RULE_NAME_LENGTH,
		maxDescriptionLength = DEFAULT_MAX_DESCRIPTION_LENGTH,
		platformOptions = [],
		groupOptions = [],
		screenLocationOptions = [],
		tagsInCurrentOrganization = [],
		handleSaveChanges,
		editedId,
		setPlatformId,
		platformError = false,
		platformErrorMessage,
		createRuleError,
		setCreateRuleError,
		lockScreenLocation = false,
		screenLocationsLoading = false,
		groupsLoading = false,
		dialogTitle = "Create Your Rule",
		submitButtonLabel = "Create Rule",
	} = props;

	const buildInitialState = (): CreateRuleFormState => ({
		ruleName: initialState?.ruleName ?? "",
		ruleDescription: initialState?.ruleDescription ?? "",
		ruleTags: initialState?.ruleTags?.length
			? initialState.ruleTags : tags ?? [],
		screenLocation: initialState?.screenLocation ?? null,
		platformId: initialState?.platformId ?? "",
		groups: initialState?.groups?.length ? initialState.groups : [],
	});

	const [formState, setFormState] = useState<CreateRuleFormState>(buildInitialState());
	const [locationPreviewOpen, setLocationPreviewOpen] = useState(false);
	const [locationPreviewUrl, setLocationPreviewUrl] = useState("");
	const [locationPreviewName, setLocationPreviewName] = useState("");
	const [platformSelectFocused, setPlatformSelectFocused] = useState(false);
	const [platformMenuOpen, setPlatformMenuOpen] = useState(false);

	useEffect(() => {
		if (!open) {
			setLocationPreviewOpen(false);
			setLocationPreviewUrl("");
			setLocationPreviewName("");
			setPlatformSelectFocused(false);
			setPlatformMenuOpen(false);
			return;
		}
		setFormState(buildInitialState());
		// Only hydrate from `initialState` when opening; do not reset on parent rerenders (e.g. platformId) or name/description would clear while typing.
		 
	}, [open]);

	const platformHasValue =
		formState.platformId !== "" && formState.platformId != null;

	const isSaveDisabled =
		!formState.ruleName.trim() ||
		!formState.ruleDescription.trim() ||
		!formState.screenLocation ||
		formState.platformId === "" ||
		formState.platformId == null;

	const handleRuleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.slice(0, maxRuleNameLength);
		setCreateRuleError?.(null);
		setFormState((previous) => ({
			...previous,
			ruleName: value
		}));
	};

	const handleRuleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.slice(0, maxDescriptionLength);
		setFormState((previous) => ({
			...previous,
			ruleDescription: value
		}));
	};

	const handleScreenLocationChange = (event: SelectChangeEvent<string>) => {
		const selectedId = event.target.value;
		const identifiedScreenLocationOption = screenLocationOptions.find((opt) => String(opt.id) === String(selectedId)) ?? null;
		setFormState((previous) => ({
			...previous,
			screenLocation: identifiedScreenLocationOption,
		}));
	};

	const handlePlatformChange = (event: SelectChangeEvent<string>) => {
		const selectedPlatformId = event.target.value;
		if (!selectedPlatformId) {
			setFormState((previous) => ({
				...previous,
				platformId: "",
				screenLocation: null,
			}));
			setPlatformId?.("");
			return;
		}

		const identifiedPlatformOption = platformOptions.find((option) => String(option.id) === String(selectedPlatformId));
		const resolvedPlatformId = identifiedPlatformOption?.id ?? selectedPlatformId;

		setFormState((previous) => ({
			...previous,
			platformId: resolvedPlatformId,
			screenLocation: null,
		}));

		setPlatformId?.(resolvedPlatformId);
	};

	const handleGroupsAutocompleteChange = (
		_event: SyntheticEvent<Element, Event>,
		newValue: RuleGroupOption[],
	) => {
		setFormState((previous) => ({
			...previous,
			groups: newValue.map((g) => ({ id: String(g.id), name: g.name })),
		}));
	};

	const setCreateTags = (updatedTags: string[]) => {
		setFormState((previous) => ({
			...previous,
			ruleTags: updatedTags
		}));

		setTags?.(updatedTags);

		// only save if we are editing an existing rule
		if (editedId && handleSaveChanges) {
			handleSaveChanges(undefined, updatedTags);
		}
	};

	const handleSave = () => {
		onSave({
			ruleName: formState.ruleName.trim(),
			ruleDescription: formState.ruleDescription.trim(),
			ruleTags: formState.ruleTags,
			screenLocation: formState.screenLocation,
			platformId: formState.platformId,
			groups: formState.groups,
		});
	};

	return (
		<>
			<CoAppDialog
				open={open}	
				fullWidth
				maxWidth="md"
				slotProps={{
					paper: {
						sx: {
							borderRadius: 4,
							padding: 0,
							maxWidth: 560,
							width: "100%",
							background: "#FFFFFF",
							boxShadow: "0 24px 64px rgba(13, 31, 48, 0.18), 0 0 0 1px rgba(13, 31, 48, 0.04)"
						}
					}
				}}
			>
				<DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
					<Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
						<Typography
							variant="h6"
							sx={{
								fontSize: 18,
								fontWeight: 600,
								letterSpacing: "-0.005em",
								color: "rgba(13, 31, 48, 0.92)"
							}}
						>
							{dialogTitle}
						</Typography>
					</Stack>
				</DialogTitle>

				<Divider sx={{ borderColor: "rgba(13, 31, 48, 0.06)" }} />

				<DialogContent sx={{ pt: 2.5, px: 3, pb: 1 }}>
					<Stack spacing={2}>
						<CoAppTextField
							fullWidth
							label="Rule Name"
							value={formState.ruleName}
							onChange={handleRuleNameChange}
							required
							helperText={`${formState.ruleName.length}/${maxRuleNameLength}` + (createRuleError ? `: ${createRuleError}` : "")}
							error={createRuleError !== null}
						/>
						<CoAppTextField
							fullWidth
							label="Rule Description"
							value={formState.ruleDescription}
							onChange={handleRuleDescriptionChange}
							required
							multiline
							minRows={3}
							maxRows={6}
							helperText={`${formState.ruleDescription.length}/${maxDescriptionLength}`}
						/>

						<Box>
							<TagInput
								createTags={setCreateTags}
								disabled={formState.ruleDescription.length < 1 || formState.ruleName.length < 1}
								textFieldWidth="100%"
								tags={formState.ruleTags}
								tagsInCurrentOrg={tagsInCurrentOrganization}
							/>
						</Box>

						{
							platformOptions.length > 0 && (
								<FormControl fullWidth size="small" sx={SELECT_FIELD_SX}>
									<InputLabel
										id="platform-label"
										shrink={platformHasValue || platformSelectFocused || platformMenuOpen}
									>
									Platform
									</InputLabel>
									<Select
										labelId="platform-label"
										MenuProps={SELECT_MENU_PROPS}
										label="Platform"
										name="platform"
										onChange={handlePlatformChange}
										disabled={lockScreenLocation}
										value={formState.platformId !== "" && formState.platformId != null ? String(formState.platformId) : ""}
										fullWidth
										onFocus={() => {
											setPlatformSelectFocused(true);
										}}
										onBlur={() => {
											setPlatformSelectFocused(false);
										}}
										onOpen={() => {
											setPlatformMenuOpen(true);
										}}
										onClose={() => {
											setPlatformMenuOpen(false);
										}}
									>
										{platformOptions.map(option => (
											<MenuItem key={option.id} value={String(option.id)}>
												{option.name}
											</MenuItem>
										))}
									</Select>
									<FormHelperText error={platformError} sx={{ marginTop: "5px" }}>
										{platformErrorMessage ?? null}
									</FormHelperText>
								</FormControl>
							)
						}

						<FormControl fullWidth size="small" sx={SELECT_FIELD_SX}>
							<InputLabel id="reaction-location-label">Screen Location</InputLabel>
							<Select
								labelId="reaction-location-label"
								MenuProps={SELECT_MENU_PROPS}
								label="Screen Location"
								value={formState.screenLocation ? String(formState.screenLocation.id) : ""}
								// QA fix: render just the location name as the selected value so it
								// stays vertically centered (the menu item's eye-preview button was
								// throwing the closed-select value off-center).
								renderValue={(selected) => {
									const loc = screenLocationOptions.find((l: ScreenLocationOption) => String(l.id) === String(selected));
									return (
										<Typography variant="body2" sx={{ display: "flex", alignItems: "center", lineHeight: 1.4 }}>
											{loc?.name ?? ""}
										</Typography>
									);
								}}
								onChange={handleScreenLocationChange}
								disabled={
									lockScreenLocation ||
								formState.platformId === "" ||
								formState.platformId == null ||
								screenLocationsLoading
								}
							>
								{
									screenLocationOptions.map((location: ScreenLocationOption) => {
										const previewUrl = normalizeLocationPreviewImageUrl(location);
										return (
											<MenuItem
												key={location.id}
												value={location.id}
												// px: 1.5 (12px) matches the field's selected-value padding
												// (SELECT_FIELD_SX → .MuiSelect-select), so the open menu's
												// text lines up with the closed field's text.
												sx={{ display: "flex", alignItems: "center", px: 1.5 }}
											>
												<Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>{location.name}</Typography>
												<IconButton
													size="small"
													edge="end"
													disabled={!previewUrl}
													aria-label={previewUrl ? `Preview ${location.name}` : "Preview unavailable"}
													// ml: auto pins every eye icon to the right edge so they
													// form one aligned column regardless of label length.
													onMouseDown={(event) => {
														event.stopPropagation();
													}}
													onClick={(event) => {
														event.stopPropagation();
														event.preventDefault();
														if (previewUrl) {
															setLocationPreviewUrl(previewUrl);
															setLocationPreviewName(location.name);
															setLocationPreviewOpen(true);
														}
													}}
													sx={{
														ml: "auto",
														flexShrink: 0,
														color: previewUrl ? "#1890FF" : "text.disabled",
													}}
												>
													<ScreenLocationPreviewEyeIcon />
												</IconButton>
											</MenuItem>
										);
									})
								}
							</Select>
						</FormControl>

						<Box sx={{ width: "100%" }}>
							<CoAppMultiSelect
								disabled={groupsLoading || groupOptions.length === 0}
								isFreeSolo={false}
								label="Groups"
								textFieldVariant="outlined"
								textFieldMargin="none"
								options={groupOptions}
								value={formState.groups}
								onChange={handleGroupsAutocompleteChange}
								customRenderOptionLabel={(option: RuleGroupOption | string) =>
									typeof option === "object" && option != null ? option.name : String(option)
								}
								customRenderCheckboxes={(
									props: HTMLAttributes<HTMLLIElement> & { key?: unknown },
									option: RuleGroupOption,
									state: AutocompleteRenderOptionState,
								) => {
									const { key: _k, ...liProps } = props;
									return (
										<li key={option.id} {...liProps}>
											<Checkbox style={{ marginRight: 8 }} checked={state.selected} size="small" />
											<ListItemText primary={option.name} />
										</li>
									);
								}}
								isOptionEqualToValue={(a: RuleGroupOption, b: RuleGroupOption) =>
									String(a.id) === String(b.id)
								}
								chipSize="small"
								customStyles={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
									"& .MuiOutlinedInput-notchedOutline": {
										borderRadius: "8px",
									},
								}}
								textFieldWidth="100%"
								placeholder={groupOptions.length > 0 ? "Add groups…" : undefined}
							/>
						</Box>

					</Stack>
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
					<CoAppCancelTextButton onClick={onClose}>Cancel</CoAppCancelTextButton>
					<CoAppActionButton onClick={handleSave} disabled={isSaveDisabled || isSaving}>
						{isSaving ? "Saving..." : submitButtonLabel}
					</CoAppActionButton>
				</DialogActions>
			</CoAppDialog>

			<ScreenLocationPreviewModal
				open={locationPreviewOpen}
				onClose={() => {
					setLocationPreviewOpen(false);
					setLocationPreviewUrl("");
					setLocationPreviewName("");
				}}
				previewUrl={locationPreviewUrl}
				locationName={locationPreviewName}
				stackingOffset={10}
			/>
		</>
	);
};

export default CreateRuleModal;